import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Typography, Button, Chip, IconButton, Tooltip, CircularProgress } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import RefreshIcon from "@mui/icons-material/Refresh";
import { toast } from "react-toastify";
import getTenantIdFromURL from "./utils/getTenantIdFromURL";
import { getApiKeyByTenantId, createApiKey, revokeApiKey } from "./api/tenantApi";

const INK = "#0B2A4A", BLUE = "#1E6FE8", GREEN = "#12A150", MUTED = "#64748b", LINE = "#e3ebf3";
const CODE_BG = "#0E2340", CODE_FG = "#CFE0F5";

const Method = ({ m }) => {
  const c = { GET: "#12A150", POST: "#1E6FE8", PUT: "#E7A13C", DELETE: "#E24B4A" }[m] || MUTED;
  return <Box component="span" sx={{ fontFamily: "monospace", fontWeight: 800, fontSize: 12, color: "#fff", background: c, borderRadius: "6px", px: 1, py: "2px", mr: 1 }}>{m}</Box>;
};

const Code = ({ children }) => (
  <Box sx={{ position: "relative", background: CODE_BG, color: CODE_FG, borderRadius: "10px", p: 2, my: 1.5, overflowX: "auto" }}>
    <IconButton size="small" onClick={() => { navigator.clipboard?.writeText(String(children)); toast.success("Copied"); }}
      sx={{ position: "absolute", top: 6, right: 6, color: "#7fa8d8" }}><ContentCopyIcon sx={{ fontSize: 15 }} /></IconButton>
    <pre style={{ margin: 0, fontFamily: "'SF Mono',ui-monospace,Menlo,monospace", fontSize: 12.5, lineHeight: 1.6, whiteSpace: "pre" }}>{children}</pre>
  </Box>
);

const Section = ({ id, title, children }) => (
  <Box id={id} sx={{ mb: 5, scrollMarginTop: 20 }}>
    <Typography sx={{ fontSize: 20, fontWeight: 800, color: INK, mb: 1.5, letterSpacing: "-.01em" }}>{title}</Typography>
    {children}
  </Box>
);

const Endpoint = ({ method, path, children }) => (
  <Box sx={{ border: `1px solid ${LINE}`, borderRadius: "12px", p: 2, mb: 2 }}>
    <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", mb: 0.5 }}>
      <Method m={method} />
      <Typography component="span" sx={{ fontFamily: "monospace", fontSize: 13.5, fontWeight: 600, color: INK }}>{path}</Typography>
    </Box>
    {children}
  </Box>
);

const P = ({ children, sx }) => <Typography sx={{ fontSize: 14, lineHeight: 1.65, color: "#334155", mb: 1.25, ...sx }}>{children}</Typography>;

const NAV = [
  ["overview", "Overview"], ["keys", "API keys"], ["auth", "Authentication"],
  ["counterparties", "Counterparties"], ["underwriting", "Underwriting"],
  ["portfolio", "Portfolio"], ["webhooks", "Webhooks"], ["errors", "Errors & limits"],
];

export default function ApiDocs() {
  const reduxTenant = useSelector((s) => s.userInfo.tenantId);
  const tenantId = getTenantIdFromURL() || reduxTenant;

  const [apiKey, setApiKey] = useState(null);
  const [keyLoading, setKeyLoading] = useState(false);
  const [keyBusy, setKeyBusy] = useState(false);

  const loadKey = useCallback(async () => {
    if (!tenantId) return;
    setKeyLoading(true);
    try {
      const res = await getApiKeyByTenantId(tenantId);
      setApiKey(res?.data?.apiKey || res?.apiKey || res?.data?.key || null);
    } catch (e) {
      setApiKey(null);
    } finally { setKeyLoading(false); }
  }, [tenantId]);

  useEffect(() => { loadKey(); }, [loadKey]);

  const genKey = async () => {
    setKeyBusy(true);
    try {
      const res = await createApiKey(tenantId);
      setApiKey(res?.data?.apiKey || res?.apiKey || res?.data?.key || null);
      toast.success("API key generated");
    } catch (e) { toast.error(e?.response?.data?.message || "Failed to generate key"); }
    finally { setKeyBusy(false); }
  };

  const revoke = async () => {
    if (!window.confirm("Revoke this API key? Any integration using it will stop working.")) return;
    setKeyBusy(true);
    try { await revokeApiKey(tenantId); setApiKey(null); toast.success("API key revoked"); }
    catch (e) { toast.error(e?.response?.data?.message || "Failed to revoke key"); }
    finally { setKeyBusy(false); }
  };

  const masked = apiKey ? `${String(apiKey).slice(0, 8)}${"•".repeat(18)}${String(apiKey).slice(-4)}` : "";

  return (
    <Box sx={{ maxWidth: 1180, mx: "auto", px: { xs: 2, md: 3 }, py: 2, display: "flex", gap: 4 }}>
      {/* side nav */}
      <Box sx={{ width: 190, flexShrink: 0, display: { xs: "none", md: "block" }, position: "sticky", top: 12, alignSelf: "flex-start" }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", color: MUTED, mb: 1 }}>CrediArc API</Typography>
        {NAV.map(([id, label]) => (
          <Box key={id} component="a" href={`#${id}`} sx={{ display: "block", fontSize: 13.5, color: "#475569", py: 0.6, textDecoration: "none", borderLeft: "2px solid transparent", pl: 1.25, "&:hover": { color: BLUE, borderColor: BLUE } }}>{label}</Box>
        ))}
      </Box>

      {/* content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
          <Typography sx={{ fontSize: 28, fontWeight: 800, color: INK, letterSpacing: "-.01em" }}>CrediArc API</Typography>
          <Chip label="v1" size="small" sx={{ height: 22, fontWeight: 700, background: "#eef2ff", color: "#3b3f8f" }} />
        </Box>
        <P sx={{ fontSize: 15.5, color: MUTED, mb: 4 }}>
          Programmatically submit counterparties, run underwriting, and pull scores, recommended limits and portfolio analytics —
          the same engine that powers the CrediArc dashboard, over a simple REST API.
        </P>

        <Section id="overview" title="Overview">
          <P><strong>Base URL</strong></P>
          <Code>https://api.crediarc.com/v1</Code>
          <P>All requests are HTTPS, accept and return <code>application/json</code>, and are authenticated with your tenant's API key. Every response includes a <code>request_id</code> for support.</P>
        </Section>

        <Section id="keys" title="Your API keys">
          <Box sx={{ border: `1px solid ${LINE}`, borderRadius: "12px", p: 2.25, background: "#fff" }}>
            {keyLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><CircularProgress size={16} /><Typography sx={{ fontSize: 13, color: MUTED }}>Loading…</Typography></Box>
            ) : apiKey ? (
              <>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <VpnKeyIcon sx={{ color: GREEN, fontSize: 20 }} />
                  <Typography sx={{ fontFamily: "monospace", fontSize: 14, color: INK, letterSpacing: ".5px" }}>{masked}</Typography>
                  <Tooltip title="Copy full key" arrow>
                    <IconButton size="small" onClick={() => { navigator.clipboard?.writeText(apiKey); toast.success("API key copied"); }}><ContentCopyIcon sx={{ fontSize: 16 }} /></IconButton>
                  </Tooltip>
                  <Button onClick={revoke} disabled={keyBusy} size="small" sx={{ ml: "auto", textTransform: "none", color: "#c0362c" }}>Revoke</Button>
                </Box>
                <Typography sx={{ fontSize: 12, color: MUTED, mt: 1 }}>Keep this secret. Treat it like a password — anyone with it can act as your tenant.</Typography>
              </>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                <Typography sx={{ fontSize: 14, color: "#334155" }}>No API key yet. Generate one to start integrating.</Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Tooltip title="Reload" arrow><span><IconButton size="small" onClick={loadKey}><RefreshIcon fontSize="small" /></IconButton></span></Tooltip>
                  <Button onClick={genKey} disabled={keyBusy || !tenantId} variant="contained" startIcon={<VpnKeyIcon />} sx={{ textTransform: "none", background: BLUE }}>Generate API key</Button>
                </Box>
              </Box>
            )}
          </Box>
        </Section>

        <Section id="auth" title="Authentication">
          <P>Pass your API key in the <code>Authorization</code> header as a bearer token on every request.</P>
          <Code>{`curl https://api.crediarc.com/v1/counterparties \\
  -H "Authorization: Bearer YOUR_API_KEY"`}</Code>
          <P>Requests without a valid key return <code>401 Unauthorized</code>.</P>
        </Section>

        <Section id="counterparties" title="Counterparties">
          <Endpoint method="POST" path="/v1/counterparties">
            <P>Create a counterparty and (optionally) kick off underwriting.</P>
            <Code>{`curl -X POST https://api.crediarc.com/v1/counterparties \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Globex Corp",
    "country": "US",
    "requested_limit": 250000,
    "underwrite": true
  }'`}</Code>
            <P sx={{ fontWeight: 600, mb: 0.5 }}>Response</P>
            <Code>{`{
  "id": "cp_9a20eec1",
  "name": "Globex Corp",
  "country": "US",
  "status": "underwriting",
  "request_id": "req_8f2c…"
}`}</Code>
          </Endpoint>

          <Endpoint method="GET" path="/v1/counterparties/{id}">
            <P>Retrieve a counterparty with its latest assessment.</P>
            <Code>{`{
  "id": "cp_9a20eec1",
  "name": "Globex Corp",
  "arc_score": 4.3,
  "risk_band": "medium",
  "recommended_limit": 180000,
  "approved_limit": null,
  "status": "assessed",
  "updated_at": "2026-07-14T10:22:00Z"
}`}</Code>
          </Endpoint>

          <Endpoint method="GET" path="/v1/counterparties">
            <P>List counterparties. Supports <code>?limit=</code>, <code>?cursor=</code>, <code>?status=</code>, and <code>?min_score=</code>.</P>
          </Endpoint>
        </Section>

        <Section id="underwriting" title="Underwriting">
          <Endpoint method="POST" path="/v1/counterparties/{id}/underwrite">
            <P>Trigger a fresh underwriting run (e.g. after uploading new financials or changing the requested limit). Returns immediately; subscribe to a webhook for completion.</P>
            <Code>{`{ "status": "underwriting", "request_id": "req_1b9d…" }`}</Code>
          </Endpoint>
        </Section>

        <Section id="portfolio" title="Portfolio">
          <Endpoint method="GET" path="/v1/portfolio/summary">
            <P>Aggregate portfolio metrics for your tenant.</P>
            <Code>{`{
  "counterparties": 785,
  "recommended_exposure": 412500000,
  "approved_exposure": 168200000,
  "avg_arc_score": 5.1,
  "risk_distribution": { "low": 210, "medium": 430, "high": 145 }
}`}</Code>
          </Endpoint>
        </Section>

        <Section id="webhooks" title="Webhooks">
          <P>Register a webhook to be notified when an assessment completes, instead of polling. We POST a signed JSON payload to your endpoint.</P>
          <Code>{`{
  "event": "counterparty.assessed",
  "data": {
    "id": "cp_9a20eec1",
    "arc_score": 4.3,
    "recommended_limit": 180000
  },
  "sent_at": "2026-07-14T10:22:03Z"
}`}</Code>
          <P>Verify the <code>X-CrediArc-Signature</code> header against your signing secret to confirm authenticity.</P>
        </Section>

        <Section id="errors" title="Errors & rate limits">
          <P>The API uses standard HTTP status codes. Errors return a machine-readable body:</P>
          <Code>{`{
  "error": { "type": "invalid_request", "message": "requested_limit must be a positive number" },
  "request_id": "req_8f2c…"
}`}</Code>
          <P><strong>Rate limits:</strong> 600 requests/minute per API key. Exceeding it returns <code>429 Too Many Requests</code> with a <code>Retry-After</code> header. Underwriting runs are asynchronous and do not count against this while processing.</P>
        </Section>

        <Typography sx={{ fontSize: 12, color: MUTED, mt: 4, pt: 3, borderTop: `1px solid ${LINE}` }}>
          Need a capability that isn't here yet? Contact your CrediArc account manager — the API is expanding.
        </Typography>
      </Box>
    </Box>
  );
}
